// EN-4743. PDF417 decoding for the back of a US / Canadian driver's licence or
// state ID, in the visitor's browser.
//
// zxing-wasm 3.1.4 (zxing-cpp compiled to WebAssembly, MIT). The engine file
// (zxing_reader.wasm, ~930 KB) comes from zxing-wasm's default CDN
// (fastly.jsdelivr.net/npm/zxing-wasm@3.1.4/...); the version is in that URL, so
// it always matches the JS. No card data goes there, only the engine download.
//
// Decoding runs in a Web Worker (pdf417.worker.ts) so the camera preview never
// freezes while a frame is decoded; on the main thread a scan pass blocked the
// page for ~70-120 ms. The worker is only created for licence / state ID
// visitors. Three modes, chosen once when the reader loads:
//   worker         the worker crops, scales, rotates and decodes (OffscreenCanvas)
//   worker-pixels  the page crops, scales and rotates; the worker decodes
//                  (iOS 15-16.3: no OffscreenCanvas 2D in workers)
//   main-thread    the worker couldn't start: decode on the page, as before
//
// Nothing here throws into the page. If the engine can't load (no WebAssembly,
// the CDN is blocked) the reader reports itself unavailable and the capture falls
// back to today's photo-only flow.
import type { Region } from '../../utils/mrz/mrzImage';
import { middlewareService } from '../middleware/middlewareService';
import {
  READ_VARIANTS,
  decodeTries,
  drawTry,
  planTries,
  type DecodeOutcome,
  type DecodeTask,
  type DecodeVariant,
} from './pdf417Decode';

export {
  SWEEP_ANGLES,
  MIN_DECODE_WIDTH,
  MAX_DECODE_WIDTH,
  WHOLE_FRAME_MAX_WIDTH,
  photoTasks,
  PHOTO_READ_BUDGET_MS,
  type DecodeTask,
  type DecodeVariant,
  type DecodeOutcome,
} from './pdf417Decode';

export type Pdf417ReaderMode = 'worker' | 'worker-pixels' | 'main-thread';

interface Backend {
  mode: Pdf417ReaderMode;
  decode: (source: CanvasImageSource, tasks: DecodeTask[], budgetMs?: number) => Promise<DecodeOutcome>;
  terminate: () => void;
}

const MISS: DecodeOutcome = { text: null, tries: 0, variant: null };

let backendPromise: Promise<Backend | null> | null = null;
let unavailableReason: string | null = null;
let currentMode: Pdf417ReaderMode | null = null;

let pageCanvas: HTMLCanvasElement | null = null;
const renderOnPage = (source: CanvasImageSource) => (t: Parameters<typeof drawTry>[2]) => {
  pageCanvas ??= document.createElement('canvas');
  return drawTry(pageCanvas, source, t);
};

const errorText = (err: unknown): string => (err instanceof Error ? err.message : String(err));

// ---- main thread (fallback) --------------------------------------------------

const startMainThread = async (): Promise<Backend> => {
  if (typeof WebAssembly !== 'object') throw new Error('no-webassembly');
  const zxing = await import('zxing-wasm/reader');
  await zxing.prepareZXingModule({ fireImmediately: true });
  const read = async (image: ImageData, variant: DecodeVariant) =>
    (await zxing.readBarcodes(image, READ_VARIANTS[variant]))[0]?.text || null;
  return {
    mode: 'main-thread',
    decode: (source, tasks, budgetMs) =>
      decodeTries(planTries(tasks), renderOnPage(source), read, budgetMs ? performance.now() + budgetMs : Infinity),
    terminate: () => undefined,
  };
};

// ---- worker ------------------------------------------------------------------

type WorkerReply =
  | { type: 'ready'; offscreen: boolean }
  | { type: 'unavailable'; reason: string }
  | { type: 'result'; id: number; text: string | null; tries: number; variant: DecodeVariant | null; ms: number };

// One bitmap per task, cropped (and, where the browser supports it, scaled) to
// the task's region, so a 4K frame never crosses to the worker whole. The task
// is rewritten to cover its bitmap.
const cropBitmaps = async (
  source: CanvasImageSource,
  tasks: DecodeTask[],
): Promise<{ frame: ImageBitmap; task: DecodeTask }[]> => {
  const items: { frame: ImageBitmap; task: DecodeTask }[] = [];
  try {
    for (const task of tasks) {
      const { x, y, w, h } = task.region;
      if (w < 1 || h < 1) continue;
      const sx = Math.round(x);
      const sy = Math.round(y);
      const sw = Math.max(1, Math.round(w));
      const sh = Math.max(1, Math.round(h));
      const maxWidth = task.maxWidth ?? Infinity;
      let frame: ImageBitmap;
      if (sw > maxWidth) {
        const resize = { resizeWidth: Math.round(maxWidth), resizeHeight: Math.round((sh * maxWidth) / sw), resizeQuality: 'high' as const };
        frame = await createImageBitmap(source as ImageBitmapSource, sx, sy, sw, sh, resize).catch(() =>
          createImageBitmap(source as ImageBitmapSource, sx, sy, sw, sh),
        );
      } else {
        frame = await createImageBitmap(source as ImageBitmapSource, sx, sy, sw, sh);
      }
      items.push({ frame, task: { ...task, region: { x: 0, y: 0, w: frame.width, h: frame.height } } });
    }
    return items;
  } catch (err) {
    items.forEach(({ frame }) => frame.close());
    throw err;
  }
};

const startWorker = (): Promise<Backend> =>
  new Promise((resolve, reject) => {
    if (typeof Worker !== 'function') {
      reject(new Error('no-worker'));
      return;
    }
    let worker: Worker;
    try {
      worker = new Worker(new URL('./pdf417.worker.ts', import.meta.url), { type: 'module' });
    } catch (err) {
      reject(err);
      return;
    }

    const pending = new Map<number, (outcome: DecodeOutcome) => void>();
    let nextId = 1;
    let ready = false;
    // Set if the worker dies after starting: every later decode is a miss.
    let broken = false;

    const failAll = () => {
      broken = true;
      pending.forEach((settle) => settle(MISS));
      pending.clear();
    };

    const request = (message: Record<string, unknown>, transfer: Transferable[]): Promise<DecodeOutcome> => {
      if (broken) return Promise.resolve(MISS);
      const id = nextId++;
      return new Promise((settle) => {
        pending.set(id, settle);
        try {
          worker.postMessage({ ...message, id }, transfer);
        } catch {
          pending.delete(id);
          settle(MISS);
        }
      });
    };

    worker.onmessage = ({ data }: MessageEvent<WorkerReply>) => {
      if (data.type === 'result') {
        pending.get(data.id)?.({ text: data.text, tries: data.tries, variant: data.variant });
        pending.delete(data.id);
        return;
      }
      if (ready) return;
      if (data.type === 'unavailable') {
        worker.terminate();
        reject(new Error(data.reason));
        return;
      }
      ready = true;
      const offscreen = data.offscreen && typeof createImageBitmap === 'function';

      // The page crops each try and the worker decodes it.
      const decodePixels = (source: CanvasImageSource, tasks: DecodeTask[], budgetMs?: number) =>
        decodeTries(
          planTries(tasks),
          renderOnPage(source),
          async (image, variant) => (await request({ type: 'decodePixels', image, variant }, [image.data.buffer])).text,
          budgetMs ? performance.now() + budgetMs : Infinity,
        );

      resolve({
        mode: offscreen ? 'worker' : 'worker-pixels',
        decode: async (source, tasks, budgetMs) => {
          if (!offscreen) return decodePixels(source, tasks, budgetMs);
          let items: { frame: ImageBitmap; task: DecodeTask }[];
          try {
            items = await cropBitmaps(source, tasks);
          } catch {
            return decodePixels(source, tasks, budgetMs);
          }
          if (items.length === 0) return MISS;
          return request({ type: 'decode', items, budgetMs }, items.map(({ frame }) => frame));
        },
        terminate: () => {
          failAll();
          worker.terminate();
        },
      });
    };

    worker.onerror = (event) => {
      event.preventDefault?.();
      if (ready) {
        failAll();
        return;
      }
      worker.terminate();
      reject(new Error(event.message || 'worker-error'));
    };

    worker.postMessage({ type: 'init' });
  });

// ---- loading -------------------------------------------------------------------

const loadBackend = async (): Promise<Backend | null> => {
  let workerFailure: string | null = null;
  try {
    const backend = await startWorker();
    currentMode = backend.mode;
    unavailableReason = null;
    middlewareService.logInfo('id_barcode_reader_mode', { mode: backend.mode });
    return backend;
  } catch (err) {
    workerFailure = errorText(err);
  }
  try {
    const backend = await startMainThread();
    currentMode = backend.mode;
    unavailableReason = null;
    middlewareService.logInfo('id_barcode_reader_mode', { mode: backend.mode, workerFailure });
    return backend;
  } catch (err) {
    unavailableReason = errorText(err);
    return null;
  }
};

/** Start loading the decoder. Safe to call more than once. True once it's ready. */
export const preloadPdf417Reader = async (): Promise<boolean> => {
  if (!backendPromise) backendPromise = loadBackend();
  return (await backendPromise) !== null;
};

/** True once the decoder is loaded; false if it failed. */
export const isPdf417ReaderReady = (): Promise<boolean> => preloadPdf417Reader();

/** Why the decoder isn't available, for telemetry. Null when it's fine. */
export const pdf417ReaderUnavailableReason = (): string | null => unavailableReason;

/** Where decoding runs, once loaded (for telemetry and tests). */
export const pdf417ReaderMode = (): Pdf417ReaderMode | null => currentMode;

/**
 * One scan pass: try each task (a region of `source` at its angles, with its
 * decoder variant) in order and return the first barcode text found, with how
 * many tries it took and which variant read it. A miss when nothing was read or
 * the decoder isn't available.
 */
export const decodePdf417 = async (
  source: CanvasImageSource,
  tasks: DecodeTask[],
  // Optional time budget: no new try starts after it (the last-chance photo read).
  budgetMs?: number,
): Promise<DecodeOutcome> => {
  if (!(await preloadPdf417Reader())) return MISS;
  const backend = await backendPromise;
  if (!backend) return MISS;
  try {
    return await backend.decode(source, tasks, budgetMs);
  } catch {
    // A failed pass is just a miss; the caller tries the next frame.
    return MISS;
  }
};

/** Same as decodePdf417, the text only. */
export const readPdf417Pass = async (source: CanvasImageSource, tasks: DecodeTask[]): Promise<string | null> =>
  (await decodePdf417(source, tasks)).text;

/** One region of `source`, at each correction angle in turn. */
export const readPdf417 = (source: CanvasImageSource, region: Region, angles: readonly number[] = [0]): Promise<string | null> =>
  readPdf417Pass(source, [{ region, angles }]);

/** Free the decoder when the visitor leaves the ID capture. */
export const terminatePdf417Reader = async (): Promise<void> => {
  const pending = backendPromise;
  backendPromise = null;
  currentMode = null;
  const backend = await pending?.catch(() => null);
  backend?.terminate();
};

/** The photo just taken (a data URL) as an image the decoder can draw from. */
export const loadPhoto = (src: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
